import { useTranslator } from '@bluebeam/react-i18n';
import AscendingSort from 'components/Icons/AscendingSort';
import DescendingSort from 'components/Icons/DescendingSort';
import Sort from 'components/Icons/Sort';
import { Button } from 'components/lib';
import OutsideClick from 'components/OutsideClick';
import React, { FC, useState } from 'react';
import { Manager, Popper, Reference } from 'react-popper';
import { SheetSortFields } from 'records/SheetQuery';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';

interface Props extends WithAppStoreProps {
  sortOptions: [string, SheetSortFields][];
  startingSortField: SheetSortFields;
  startingSortDirection: 'ascending' | 'descending';
}

const SortButton: FC<Props> = ({
  sortOptions,
  startingSortField,
  startingSortDirection,
  appState,
  actions,
}) => {
  const { translate } = useTranslator(['publishedTab', 'buttons']);
  const [open, setOpen] = useState(false);
  const [sortDirection, setSortDirection] = useState(startingSortDirection);
  const [sortField, setSortField] = useState(startingSortField);
  const rowStyle = {
    paddingLeft: 24,
    paddingTop: 14,
    paddingBottom: 14,
    height: 44,
    alignItems: 'center',
  };

  const handleSortDirectionChanged = (): void => {
    setSortDirection((prevSortDirection) =>
      prevSortDirection === 'ascending' ? 'descending' : 'ascending'
    );
  };

  const handleSortDisciplineChanged = (discipline: SheetSortFields): void => {
    setSortField(discipline);
  };

  const handleSortButtonClick = (): void => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleOutsideClick = (): void => {
    setOpen(false);
  };

  const handleApplySort = (): void => {
    const { sheetQuery } = appState;
    const newSheetQuery = sheetQuery
      .set('sortField', sortField)
      .set('sortDirection', sortDirection);
    actions.setSheetQuery(newSheetQuery);
  };

  const handleResetSort = (): void => {
    const { sheetQuery } = appState;
    const newSheetQuery = sheetQuery
      .set('sortField', 'discipline')
      .set('sortDirection', 'ascending');
    actions.setSheetQuery(newSheetQuery);
    setSortField('discipline');
    setSortDirection('ascending');
  };

  return (
    <Manager>
      <Reference>
        {({ ref }): React.ReactNode => (
          <div ref={ref} className="hs2 flex items-center">
            <Button
              id="drawings-sort-btn"
              className="row items-center justify-between mr4"
              onClick={handleSortButtonClick}
              size="small"
              variant="tertiary"
            >
              <div className="mr2">
                <Sort width="14" height="14" />
              </div>
              <div className="font-size-4">{translate('buttons:sort')}</div>
            </Button>
          </div>
        )}
      </Reference>
      <Popper>
        {({ ref, style, placement }): React.ReactNode => {
          return open ? (
            <div
              ref={ref}
              className="bgd-white b-3"
              style={{
                ...style,
                minWidth: '300px',
                zIndex: 2,
              }}
              data-placement={placement}
            >
              <OutsideClick onOutsideClick={handleOutsideClick}>
                <div className="column color-primary-dark">
                  <div
                    className="bb-2 w100 bold font-size-4 black-2"
                    style={rowStyle}
                  >
                    {translate('publishedTab:sortButton.sortBy')}
                  </div>
                  {sortOptions.map((s) =>
                    s[1] === sortField ? (
                      <div
                        key={`sheet-sorting-row-${s[1]}`}
                        className="bb-3 w100 bold font-size-4 bgd-primary-blue-opacity-10 cursor-pointer row justify-between"
                        onClick={handleSortDirectionChanged}
                        style={rowStyle}
                      >
                        {s[0]}
                        <div className="mr3">
                          {sortDirection === 'ascending' ? (
                            <DescendingSort width="20" height="20" />
                          ) : (
                            <AscendingSort width="20" height="20" />
                          )}
                        </div>
                      </div>
                    ) : (
                      <div
                        key={`sheet-sorting-row-${s[1]}`}
                        className="bb-3 w100 font-size-4 black-2 cursor-pointer"
                        onClick={() => {
                          handleSortDisciplineChanged(s[1]);
                        }}
                        style={rowStyle}
                      >
                        {s[0]}
                      </div>
                    )
                  )}
                </div>
                <div className="row justify-between p3">
                  <button
                    className="btn-sm btn-outline-primary mr1"
                    onClick={handleResetSort}
                    disabled={
                      sortField === 'discipline' &&
                      sortDirection === 'ascending'
                    }
                  >
                    {translate('buttons:reset')}
                  </button>
                  <button
                    className="btn-sm btn-primary"
                    onClick={handleApplySort}
                    disabled={
                      sortField === appState.sheetQuery.sortField &&
                      sortDirection === appState.sheetQuery.sortDirection
                    }
                  >
                    {translate('buttons:apply')}
                  </button>
                </div>
              </OutsideClick>
            </div>
          ) : null;
        }}
      </Popper>
    </Manager>
  );
};

export default withAppStore<Props>(SortButton);
